import { AsyncPipe, CommonModule } from '@angular/common';
import { Component, ElementRef, Input, ViewChild, inject } from '@angular/core';
import {
  ControlValueAccessor,
  FormControl,
  FormsModule,
  NgControl,
  ReactiveFormsModule,
} from '@angular/forms';
import {
  MatAutocompleteModule,
  MatAutocompleteSelectedEvent,
} from '@angular/material/autocomplete';
import { MatChipsModule } from '@angular/material/chips';
import { ErrorStateMatcher, MatCommonModule } from '@angular/material/core';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { Observable, map, startWith } from 'rxjs';
import { BaseModel } from '../models/base.model';
import { ErrorMessagesPipe } from '../pipes/error-messages.pipe';
import { DefaultErrorStateMatcher } from '../validation/DefaultErrorStateMatcher';

@Component({
    selector: 'cerrix-autocomplete',
    templateUrl: './cerrix-autocomplete.component.html',
    styleUrl: './cerrix-autocomplete.component.scss',
    host: { class: 'cerrix-autocomplete' },
    imports: [
        CommonModule,
        ErrorMessagesPipe,
        FormsModule,
        MatFormFieldModule,
        MatChipsModule,
        MatIconModule,
        MatInputModule,
        MatAutocompleteModule,
        ReactiveFormsModule,
        AsyncPipe,
        MatCommonModule,
        MatDividerModule,
    ],
    providers: []
})
export class CerrixAutocompleteComponent implements ControlValueAccessor {
  @Input() public placeholder: string = '';
  @Input() public type: 'primary' | 'success' = 'primary';
  @Input() public disabled: boolean = false;
  @Input() public showDeleteWarning: boolean = false;
  @Input() public useVisibility: boolean = false;
  @Input() public dataUiid: string = 'undefined';

  @Input() public checkDeletable!: (guid: string) => Promise<boolean>;

  private _options: BaseModel[] = [];
  @Input()
  set options(value: BaseModel[]) {
    if (this._options === value) {
      return;
    }

    this._options = value;
    this.setSelection();
    this.triggerFilter();
  }
  get options() {
    return this._options;
  }

  private _selected: string[] = [];
  @Input()
  set selected(value: string[]) {
    if (this._selected === value) {
      return;
    }

    this._selected = value ?? [];
    this.setSelection();
  }
  get selected() {
    return this._selected;
  }

  @ViewChild('searchInput') searchInput!: ElementRef<HTMLInputElement>;

  protected selectedOptions: BaseModel[] = [];
  protected formControl = new FormControl('');
  protected filteredOptions: Observable<BaseModel[]>;

  protected errorMatcher!: ErrorStateMatcher;
  protected ngControl = inject(NgControl, { optional: true, self: true });

  constructor() {
    if (this.ngControl != null) {
      this.ngControl.valueAccessor = this;
      this.errorMatcher = new DefaultErrorStateMatcher();
    }

    this.filteredOptions = this.formControl.valueChanges.pipe(
      startWith(null),
      map((option: string | null) =>
        option && typeof option === 'string'
          ? this._filter(option)
          : this.options?.slice()
      )
    );
  }

  public clearInput(): void {
    this.searchInput.nativeElement.value = '';
  }

  private onValueChange = (value: string[]) => {};
  private onTouched = () => {};

  public writeValue(value: string[]): void {
    this.selected = value;
  }
  public registerOnChange(onChange: (value: string[]) => void): void {
    this.onValueChange = onChange;
  }
  public registerOnTouched(onTouched: () => void): void {
    this.onTouched = onTouched;
  }
  public setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  protected onSelected(event: MatAutocompleteSelectedEvent): void {
    const index = this.selected.indexOf(event.option.value);

    if (index !== -1) {
      this.selected.splice(index, 1);
    } else {
      this.selected.push(event.option.value);
    }

    this.selected = [...this.selected];
    this.onValueChange(this.selected);

    this.clearInput();
    this.formControl.setValue(null);
  }

  public async remove(option: BaseModel): Promise<void> {
    const index = this.selected.indexOf(option.id);
    if (this.showDeleteWarning) {
      const deletable = await this.checkDeletable(option.id);
      if (deletable) {
        if (index >= 0) {
          this.selected.splice(index, 1);
          this.selected = [...this.selected];
          this.onValueChange(this.selected);
        }
      }
    } else {
      if (index >= 0) {
        this.selected.splice(index, 1);
        this.selected = [...this.selected];
        this.onValueChange(this.selected);
      }
    }
  }

  private _filter(value: string): BaseModel[] {
    const filterValue = value.toLowerCase();

    return this.options.filter(o => o.name.toLowerCase().includes(filterValue));
  }

  private setSelection(): void {
    if (this.options) {
      this.selectedOptions = this.options.filter(o =>
        this.selected.some(id => id === o.id)
      );
    }
  }

  private triggerFilter(): void {
    this.formControl.patchValue(this.formControl.value, { emitEvent: true });
  }

  onCustomClick(event: MouseEvent): void {
    this.triggerInputElementClick();
  }

  triggerInputElementClick(): void {
    const searchInputElement = this.searchInput.nativeElement;
    if (searchInputElement) {
      searchInputElement.focus();
      searchInputElement.select();
    }
  }
}
